'use client';

/**
 * TripNightsPlanner — "Plan my nights": the trip laid out by day, and for
 * every night a short list of campsites near that stop that suit the rig.
 *
 * Pure presentation. The plan comes from utils/nightPlanner, the campsite
 * picks from /api/routing/suggestions (fetched by the explorer), and picking
 * one calls back so the night's stop becomes that campsite.
 */

import React, { useEffect, useRef, useState } from 'react';
import { withBase } from '../lib/brand';
import { suggestedSlotLabel, SUGGESTED_STOP_NAME, type NightPlan, type NightSlot, type PlanDay } from '../utils/nightPlanner';
import type { SuggestionCandidate } from '../utils/routingApi';

export type SlotSuggestions = SuggestionCandidate[] | 'loading' | 'error';

interface TripNightsPlannerProps {
  plan: NightPlan;
  vehicleType: string;
  maxDriveHours: number;
  suggestions: Record<string, SlotSuggestions>;
  onPick: (slot: NightSlot, candidate: SuggestionCandidate) => void;
  /** Add a suggested halfway overnight as a plain town stop (no campsite yet). */
  onAddSuggestedStop: (slot: NightSlot) => void;
  /** Slot to open on mount (deep link / "add to tonight" flows). */
  focusSlotKey?: string | null;
  /** Shown when the trip has no departure date. */
  onPickDate?: () => void;
  hasStartDate: boolean;
}

const ENTITY_TYPE_LABEL: Record<string, string> = {
  caravan_park: 'Caravan park',
  campground: 'Campground',
  free_camping: 'Free camp',
  accom: 'Accommodation',
  accommodation: 'Accommodation',
};

const LONG_RIGS = new Set(['caravan', 'motorhome']);

export function campsiteSlug(name: string): string {
  return (name || 'campsite')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .replace(/-+/g, '-');
}

const formatDrive = (minutes?: number): string => {
  if (typeof minutes !== 'number' || Number.isNaN(minutes)) return '';
  if (minutes < 60) return `${minutes} min`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${h}h${m ? ` ${m}m` : ''}`;
};

const formatDate = (iso: string | null): string | null => {
  if (!iso) return null;
  const d = new Date(`${iso}T12:00:00`);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short' });
};

const nightsLabel = (slot: NightSlot) =>
  slot.nights > 1 ? `Nights ${slot.night}–${slot.night + slot.nights - 1}` : `Night ${slot.night}`;

function CampCard({
  c,
  slot,
  picked,
  onPick,
}: {
  c: SuggestionCandidate;
  slot: NightSlot;
  picked: boolean;
  onPick: () => void;
}) {
  const type = ENTITY_TYPE_LABEL[c.entity_type ?? ''] ?? 'Campsite';
  const facts = [
    type,
    c.has_powered ? '⚡ Powered sites' : null,
    typeof c.distance_km === 'number' ? `${c.distance_km < 1 ? '<1' : Math.round(c.distance_km)} km away` : null,
    typeof c.min_price === 'number' && c.min_price > 0 ? `from $${Math.round(c.min_price)}` : null,
  ].filter(Boolean);
  return (
    <li
      className={`rounded-lg border bg-white p-2.5 ${picked ? 'border-emerald-400 ring-1 ring-emerald-300' : 'border-stone-200'}`}
      data-testid="night-camp-card"
    >
      <div className="flex items-start justify-between gap-2">
        <a
          href={withBase(`/camp-sites/${campsiteSlug(c.name)}`)}
          target="_blank"
          rel="noopener"
          className="min-w-0 text-[12.5px] font-semibold leading-tight text-stone-900 hover:underline"
        >
          {c.name}
        </a>
        {typeof c.rating === 'number' && c.rating > 0 && (
          <span className="flex-none text-[11px] font-semibold text-stone-700">★ {Number(c.rating).toFixed(1)}</span>
        )}
      </div>
      <p className="mt-0.5 text-[10.5px] leading-snug text-stone-500">{facts.join(' · ')}</p>
      <div className="mt-2 flex items-center gap-1.5">
        <button
          type="button"
          onClick={onPick}
          disabled={picked}
          className={`h-7 min-h-0 rounded-md px-2.5 text-[11px] font-semibold transition ${
            picked
              ? 'cursor-default bg-emerald-50 text-emerald-700'
              : 'bg-[#1558b0] text-white hover:bg-accent'
          }`}
        >
          {picked ? `✓ ${nightsLabel(slot)}` : `Stay here · ${nightsLabel(slot).toLowerCase()}`}
        </button>
        {c.booking_url ? (
          <a
            href={c.booking_url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex h-7 items-center rounded-md border border-emerald-300 px-2.5 text-[11px] font-semibold text-emerald-700 hover:bg-emerald-50"
          >
            Book ↗
          </a>
        ) : (
          <a
            href={withBase(`/camp-sites/${campsiteSlug(c.name)}`)}
            target="_blank"
            rel="noopener"
            className="inline-flex h-7 items-center rounded-md border border-stone-200 px-2.5 text-[11px] font-medium text-stone-600 hover:bg-stone-50"
          >
            Details
          </a>
        )}
      </div>
    </li>
  );
}

function NightBlock({
  slot,
  vehicleType,
  maxDriveHours,
  suggestions,
  onPick,
  onAddSuggestedStop,
  defaultOpen,
}: {
  slot: NightSlot;
  vehicleType: string;
  maxDriveHours: number;
  suggestions: SlotSuggestions | undefined;
  onPick: (c: SuggestionCandidate) => void;
  onAddSuggestedStop: () => void;
  defaultOpen: boolean;
}) {
  const hasCamp = typeof slot.entityId === 'number';
  const [open, setOpen] = useState(defaultOpen || !hasCamp);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (defaultOpen) {
      setOpen(true);
      ref.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }, [defaultOpen]);

  const rig = LONG_RIGS.has(vehicleType) ? `${vehicleType}-friendly` : 'nearby';
  return (
    <div
      ref={ref}
      className={`mt-1.5 rounded-lg border px-2.5 py-2 ${slot.suggested ? 'border-amber-300 bg-amber-50/60' : 'border-stone-200 bg-stone-50/70'}`}
      data-testid="night-slot"
      data-slot-key={slot.key}
    >
      <div className="flex items-center justify-between gap-2">
        <p className="min-w-0 text-[11.5px] font-semibold text-stone-800">
          <span className="mr-1 text-[10px] font-bold uppercase tracking-wide text-amber-700">{nightsLabel(slot)}</span>
          {slot.suggested ? suggestedSlotLabel(slot.name) : slot.name}
        </p>
        {hasCamp && !slot.suggested && (
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="h-6 min-h-0 flex-none text-[11px] font-medium text-sky-700 hover:underline"
          >
            {open ? 'Hide' : 'Change camp'}
          </button>
        )}
      </div>
      {slot.suggested && (
        <div className="mt-1 flex flex-wrap items-center gap-2 text-[11px] text-amber-800">
          <span>That stretch is over {maxDriveHours}h — break it here.</span>
          <button
            type="button"
            onClick={onAddSuggestedStop}
            className="h-6 min-h-0 rounded-md border border-amber-400 bg-white px-2 font-semibold text-amber-800 hover:bg-amber-100"
          >
            Add {slot.name === SUGGESTED_STOP_NAME ? 'this stop' : slot.name}
          </button>
        </div>
      )}
      {hasCamp && !open && (
        <p className="mt-0.5 text-[10.5px] text-emerald-700">✓ Campsite picked</p>
      )}
      {open && (
        <div className="mt-1.5">
          {suggestions === 'loading' || suggestions === undefined ? (
            <ul className="space-y-1.5" aria-busy="true">
              {[0, 1, 2].map((i) => (
                <li key={i} className="h-[74px] animate-pulse rounded-lg border border-stone-200 bg-white" />
              ))}
            </ul>
          ) : suggestions === 'error' ? (
            <p className="text-[11px] text-stone-500">Couldn’t load campsites for this night just now.</p>
          ) : suggestions.length === 0 ? (
            <p className="text-[11px] text-stone-500">No {rig} campsites within 30 km — try a nearby town.</p>
          ) : (
            <>
              <p className="mb-1 text-[10.5px] text-stone-500">
                {hasCamp ? `Other ${rig} camps nearby:` : `Best ${rig} camps near ${slot.name}:`}
              </p>
              <ul className="space-y-1.5">
                {suggestions.map((c) => (
                  <CampCard
                    key={c.entity_id}
                    c={c}
                    slot={slot}
                    picked={slot.entityId === c.entity_id}
                    onPick={() => onPick(c)}
                  />
                ))}
              </ul>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function DayRow({ day, children }: { day: PlanDay; children?: React.ReactNode }) {
  const date = formatDate(day.date);
  return (
    <li className="border-b border-stone-100 px-3 py-2 last:border-b-0" data-testid="plan-day">
      <div className="flex items-baseline justify-between gap-2">
        <p className="text-[11px] font-bold uppercase tracking-wide text-sky-700">
          Day {day.index + 1}
          {date && <span className="ml-1.5 font-medium normal-case tracking-normal text-sky-600">{date}</span>}
        </p>
        {day.kind === 'drive' && (
          <p className="flex-none text-[10.5px] tabular-nums text-stone-500">
            {typeof day.distanceKm === 'number' && day.distanceKm > 0 ? `${day.distanceKm} km · ` : ''}
            {formatDrive(day.driveMinutes)}
          </p>
        )}
      </div>
      <p className="mt-0.5 text-[12.5px] text-stone-800">
        {day.kind === 'rest' ? (
          <>Rest day at <span className="font-semibold">{day.toName}</span></>
        ) : (
          <>
            {day.fromName} → <span className="font-semibold">{day.toName}</span>
            {day.slot === null && <span className="ml-1 text-[11px] text-emerald-700">· arrive</span>}
          </>
        )}
      </p>
      {day.viaNames.length > 0 && (
        <p className="text-[10.5px] text-stone-500">via {day.viaNames.join(', ')}</p>
      )}
      {children}
    </li>
  );
}

export default function TripNightsPlanner({
  plan,
  vehicleType,
  maxDriveHours,
  suggestions,
  onPick,
  onAddSuggestedStop,
  focusSlotKey,
  onPickDate,
  hasStartDate,
}: TripNightsPlannerProps) {
  if (!plan.days.length) return null;
  const driveDays = plan.days.length;
  return (
    <section className="mt-2 overflow-hidden rounded-xl border border-stone-200 bg-white" aria-label="Your days">
      <header className="flex items-center justify-between gap-2 border-b border-stone-100 bg-amber-50/50 px-3 py-2">
        <p className="text-[12px] font-semibold text-stone-900">
          {driveDays} day{driveDays === 1 ? '' : 's'} · {plan.totalNights} night{plan.totalNights === 1 ? '' : 's'}
          <span className="ml-1 font-normal text-stone-500">· up to {maxDriveHours}h driving a day</span>
        </p>
        {!hasStartDate && onPickDate && (
          <button type="button" onClick={onPickDate} className="h-6 min-h-0 text-[11px] font-medium text-sky-700 hover:underline">
            Add dates
          </button>
        )}
      </header>
      <ol>
        {plan.days.map((day) => {
          // A night block sits under the day you ARRIVE (not on rest days).
          const showSlot = day.kind === 'drive' && day.slot;
          return (
            <DayRow key={day.index} day={day}>
              {showSlot && day.slot && (
                <NightBlock
                  slot={day.slot}
                  vehicleType={vehicleType}
                  maxDriveHours={maxDriveHours}
                  suggestions={suggestions[day.slot.key]}
                  onPick={(c) => onPick(day.slot!, c)}
                  onAddSuggestedStop={() => onAddSuggestedStop(day.slot!)}
                  defaultOpen={focusSlotKey === day.slot.key}
                />
              )}
            </DayRow>
          );
        })}
      </ol>
    </section>
  );
}
